import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Grid, Link, Typography, Divider } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { CompanyDto, CompanyInsuranceCompanyInformationDto, CompanyAddressDto } from 'dtos'
import { useEffect, useState } from 'react'
import InsuranceCompanyAddEditDialog from './InsuranceCompanyAddEditDialog'
import * as controllers from 'controllers'
import { enqueueSnackbar } from 'notistack'
import { Chip } from 'components/chips'
import { CompanyAddressesParameters } from 'parameters'
import { useAuth } from 'context'

interface InsuranceCompanyCardProps {
  company: CompanyDto
}

export default function InsuranceCompanyListCard({ company }: InsuranceCompanyCardProps) {
  const { permissions } = useAuth()

  const [companyInsuranceCompanyInformation, setCompanyInsuranceCompanyInformation] =
    useState<CompanyInsuranceCompanyInformationDto>(new CompanyInsuranceCompanyInformationDto())
  const [insuranceCompanyAddEditDialogOpen, setInsuranceCompanyAddEditDialogOpen] = useState<boolean>(false)
  const [isGettingCompanyInsuranceCompanyInformation, setIsGettingCompanyInsuranceCompanyInformation] = useState<boolean>(true)
  const [isSavingCompanyInsuranceCompanyInformation, setIsSavingCompanyInsuranceCompanyInformation] = useState<boolean>(false)
  const [needsAddress, setNeedsAddress] = useState<boolean>(false)
  const [needsEmail, setNeedsEmail] = useState<boolean>(false)
  const [needsPhone, setNeedsPhone] = useState<boolean>(false)
  const [isGettingCompanyAddresses, setIsGettingCompanyAddress] = useState<boolean>(false)

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingCompanyInsuranceCompanyInformation(true)
      controllers
        .getCompanyInsuranceCompanyInformation(company.companyGuid)
        .then(response => {
          setCompanyInsuranceCompanyInformation(response.value)
        })
        .finally(() => {
          setIsGettingCompanyInsuranceCompanyInformation(false)
        })
    }
  }, [company])

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingCompanyAddress(true)
      controllers
        .getCompanyAddresses(company.companyGuid, { page: 0, pageSize: 3, statuses: ['isInsuranceUnderwriterCompanyAddress'] })
        .then(response => {
          const addresses = response.value
          setNeedsAddress(response.value.length === 0)
          const hasEmail = addresses.some((address: CompanyAddressDto) => !!address.email)
          const hasPhone = addresses.some((address: CompanyAddressDto) => !!address.phone)
          setNeedsEmail(!hasEmail)
          setNeedsPhone(!hasPhone)
        })
        .finally(() => {
          setIsGettingCompanyAddress(false)
        })
    }
  }, [company.companyGuid])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <InsuranceCompanyAddEditDialog
          initialValues={companyInsuranceCompanyInformation}
          loading={isSavingCompanyInsuranceCompanyInformation}
          onClose={() => {
            setInsuranceCompanyAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingCompanyInsuranceCompanyInformation(true)
            controllers
              .updateCompanyInsuranceCompanyInformation(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Insurance Company Information Saved Successfully!', { variant: 'success' })
                setCompanyInsuranceCompanyInformation(response.value)
                setInsuranceCompanyAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsSavingCompanyInsuranceCompanyInformation(false)
              })
          }}
          open={insuranceCompanyAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between' spacing={2}>
              <Grid item container alignItems='center' spacing={1} xs>
                <Grid item xs={12} sm='auto'>
                  <Typography fontWeight='bold' variant='h5'>
                    Insurance Company
                  </Typography>
                </Grid>

                {!isGettingCompanyAddresses && needsAddress && (
                  <Grid item xs={12} sm='auto'>
                    <Chip variant='red'>Needs Address</Chip>
                  </Grid>
                )}

                {needsEmail && (
                  <Grid item xs={12} sm='auto'>
                    <Chip variant='red'>Needs Email</Chip>
                  </Grid>
                )}

                {needsPhone && (
                  <Grid item xs={12} sm='auto'>
                    <Chip variant='red'>Needs Phone</Chip>
                  </Grid>
                )}
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setInsuranceCompanyAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingCompanyInsuranceCompanyInformation && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingCompanyInsuranceCompanyInformation && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Underwriter Company Name'
                    value={companyInsuranceCompanyInformation.insuranceUnderwriterCompanyName}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Surety Code' value={companyInsuranceCompanyInformation.insuranceSuretyCode} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant='body2'>Surety Image Seal</Typography>

                  {!companyInsuranceCompanyInformation.insuranceSuretyImageSealFileName && (
                    <Typography color='error' variant='body1'>
                      No file uploaded
                    </Typography>
                  )}

                  {companyInsuranceCompanyInformation.insuranceSuretyImageSealFileName && (
                    <Typography color='primary' variant='body1'>
                      <Link
                        onClick={() => {
                          controllers.getCompanyInsuranceCompanyInformationSuretyImageSealFile(
                            company.companyGuid!,
                            companyInsuranceCompanyInformation.insuranceSuretyImageSealFileName!
                          )
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {companyInsuranceCompanyInformation.insuranceSuretyImageSealFileName}
                      </Link>
                    </Typography>
                  )}
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||This card only shows if the company is marked as an Insurance Company.|| If there is no address, email and/or
                phone number the card will indicate it via red text. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
